import React, { useMemo } from "react"
import { X } from "lucide-react"
import LearnerField from "../../../../component/student/LearnerField"
import LearnerSelect from "../../../../component/student/LearnerSelect"
import type { LearnerSelectOption } from "../../../../component/student/LearnerSelect"
import { DEFAULT_NOTE_SUBJECT, type NoteDraft } from "../types"
import NoteColorPicker from "./NoteColorPicker"
import "./NoteComposer.style.scss"

interface NoteComposerProps {
  draft: NoteDraft
  saving: boolean
  isEditing: boolean
  subjectOptions: LearnerSelectOption[]
  onChange: (patch: Partial<NoteDraft>) => void
  onCancel: () => void
  onSubmit: () => void
}

const NoteComposer = ({
  draft,
  saving,
  isEditing,
  subjectOptions,
  onChange,
  onCancel,
  onSubmit,
}: NoteComposerProps) => {
  const options = useMemo(() => {
    const next = [...subjectOptions]
    if (!next.some((option) => option.value === DEFAULT_NOTE_SUBJECT)) {
      next.unshift({ value: DEFAULT_NOTE_SUBJECT, label: DEFAULT_NOTE_SUBJECT })
    }
    if (
      draft.subject &&
      !next.some((option) => option.value === draft.subject)
    ) {
      next.push({ value: draft.subject, label: draft.subject })
    }
    return next
  }, [draft.subject, subjectOptions])

  return (
    <section className="note-composer">
      <div className="note-composer__header">
        <h2>{isEditing ? "Edit Note" : "Create New Note"}</h2>
        <button type="button" aria-label="Close" onClick={onCancel}>
          <X size={16} />
        </button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <div className="note-composer__row">
          <LearnerField
            id="note-title"
            label="Title"
            name="title"
            value={draft.title}
            placeholder="E.g., Newton's Laws"
            disabled={saving}
            autoFocus
            onChange={(event) => onChange({ title: event.target.value })}
          />
          <LearnerSelect
            id="note-subject"
            label="Subject"
            name="subject"
            value={draft.subject}
            options={options}
            placeholder="Select a subject"
            disabled={saving}
            onChange={(event) => onChange({ subject: event.target.value })}
          />
        </div>
        <div className="learner-field">
          <label htmlFor="note-content" className="learner-field__label">
            Content
          </label>
          <textarea
            id="note-content"
            name="content"
            className="learner-field__input note-composer__content"
            value={draft.content}
            placeholder="Type your notes here..."
            disabled={saving}
            rows={4}
            onChange={(event) => onChange({ content: event.target.value })}
          />
        </div>
        <NoteColorPicker
          value={draft.color}
          disabled={saving}
          onChange={(color) => onChange({ color })}
        />
        <div className="note-composer__actions">
          <button
            type="button"
            className="note-composer__cancel"
            disabled={saving}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button type="submit" className="note-composer__save" disabled={saving}>
            {saving ? "Saving…" : isEditing ? "Save Changes" : "Save Note"}
          </button>
        </div>
      </form>
    </section>
  )
}

export default NoteComposer
